import {
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  CatalogState,
  ConditionDraft,
  ConditionError,
  emptyCondition,
  fieldFor,
  isUnary,
  validateCondition,
  withCompareTo,
  withDataKey,
} from './conditionForm';
import messages from './messages';
import { useStyles } from './styles';

export interface ConditionBuilderProps {
  conditions: ConditionDraft[];
  catalog: CatalogState;
  /** Errors are only surfaced once the admin has tried to save. */
  showErrors: boolean;
  onChange: (conditions: ConditionDraft[]) => void;
  /** Retries the catalog fetch; the builder is unusable without it. */
  onRetryCatalog: () => void;
  /** Distinguishes one level's rows from another's in the DOM. */
  idPrefix: string;
}

/** Which helper text a row's error maps to, so the message lives in one place. */
const ERROR_MESSAGES: Record<ConditionError, keyof typeof messages> = {
  no_data_key: 'conditionNeedsField',
  unknown_data_key: 'conditionFieldUnavailable',
  no_operator: 'conditionNeedsOperator',
  unsupported_operator: 'conditionOperatorUnavailable',
  no_value: 'conditionNeedsValue',
  no_compare_key: 'conditionNeedsCompareKey',
};

/**
 * The conditions deciding whether a level applies (AXHE-3929).
 *
 * A condition compares a field either to a literal or to **another field**, and
 * the second mode is the one that needed solving: comparing arbitrary fields is
 * incomprehensible, so the compare-with list is restricted to fields of the same
 * type and never contains the field being configured. That restriction comes
 * from the server (`comparable_keys`) rather than being re-derived here — see
 * `conditionForm.ts`.
 *
 * Operators are likewise type-derived: the catalog says which ones this field's
 * type allows, so the builder cannot offer `contains` on a number and then have
 * the save rejected.
 *
 * Nothing is editable until the catalog is `ready`. An empty field picker with
 * no explanation reads as "there is nothing to gate on here", which is the one
 * thing a failed fetch does not mean — and on an existing rule it would render
 * live conditions as blank selects on a compliance surface. Same reasoning as
 * the disabled compare-with toggle one level down: say why, rather than leave a
 * dead control.
 */
export const ConditionBuilder: React.FC<ConditionBuilderProps> = ({
  conditions,
  catalog,
  showErrors,
  onChange,
  onRetryCatalog,
  idPrefix,
}) => {
  const styles = useStyles();
  const intl = useIntl();

  const update = (key: string, next: ConditionDraft) =>
    onChange(conditions.map(row => (row.key === key ? next : row)));

  const heading = (
    <Typography variant="subtitle2">
      <FormattedMessage {...messages.conditionsLabel} />
    </Typography>
  );

  if (catalog.status === 'loading') {
    return (
      <div className={styles.conditions}>
        {heading}
        <div className={styles.conditionsPlaceholder}>
          <CircularProgress
            size={20}
            aria-label={intl.formatMessage(messages.conditionsLoading)}
          />
          <Typography variant="subtitle1" color="textSecondary">
            <FormattedMessage {...messages.conditionsLoading} />
          </Typography>
        </div>
      </div>
    );
  }

  if (catalog.status === 'error') {
    return (
      <div className={styles.conditions}>
        {heading}
        {/* The rest of the panel still works — a level's approvers can be edited
            and saved, and `toPayload` re-emits these conditions as they were.
            Only the builder is unavailable, and it says so with a way back. */}
        <div className={styles.error} role="alert">
          <Typography variant="body1">
            <FormattedMessage {...messages.conditionsLoadError} />
          </Typography>
          <Button variant="outlined" onClick={onRetryCatalog}>
            <FormattedMessage {...messages.retry} />
          </Button>
        </div>
      </div>
    );
  }

  const { catalog: fieldCatalog } = catalog;

  return (
    <div className={styles.conditions}>
      {heading}

      {conditions.map(condition => {
        const field = fieldFor(fieldCatalog, condition.dataKeyName);
        const error = showErrors
          ? validateCondition(condition, fieldCatalog)
          : undefined;
        // Only the fields the server says are comparable with this one. Empty is
        // a real answer — a lone date field has nothing to compare against.
        const comparable = field?.comparable_keys ?? [];
        const unary = isUnary(fieldCatalog, condition.operator);

        return (
          <div
            key={condition.key}
            className={styles.conditionRow}
            data-testid="condition-row"
          >
            <TextField
              id={`${idPrefix}-field-${condition.key}`}
              label={intl.formatMessage(messages.conditionFieldLabel)}
              value={condition.dataKeyName}
              onChange={event =>
                update(
                  condition.key,
                  withDataKey(condition, event.target.value, fieldCatalog),
                )
              }
              error={error === 'no_data_key' || error === 'unknown_data_key'}
              helperText={
                error === 'no_data_key' || error === 'unknown_data_key'
                  ? intl.formatMessage(messages[ERROR_MESSAGES[error]])
                  : undefined
              }
              select
              fullWidth
            >
              {fieldCatalog.fields.map(option => (
                <MenuItem key={option.key} value={option.key}>
                  {option.label ?? option.key}
                </MenuItem>
              ))}
            </TextField>

            {/* Operators come from the chosen field's type, so this stays empty
                until a field is picked rather than offering every operator in
                the vocabulary. */}
            <TextField
              id={`${idPrefix}-operator-${condition.key}`}
              label={intl.formatMessage(messages.conditionOperatorLabel)}
              value={condition.operator}
              onChange={event =>
                update(condition.key, { ...condition, operator: event.target.value })
              }
              error={error === 'no_operator' || error === 'unsupported_operator'}
              helperText={
                error === 'no_operator' || error === 'unsupported_operator'
                  ? intl.formatMessage(messages[ERROR_MESSAGES[error]])
                  : undefined
              }
              disabled={!field}
              select
              fullWidth
            >
              {(field?.operators ?? []).map(operator => (
                <MenuItem key={operator} value={operator}>
                  {operator}
                </MenuItem>
              ))}
            </TextField>

            {/* A unary operator tests for existence, so there is no right-hand
                side to configure and the toggle would be meaningless. */}
            {!unary && (
              <FormControlLabel
                control={
                  <Switch
                    checked={condition.compareTo === 'dataKey'}
                    disabled={!field || comparable.length === 0}
                    onChange={event =>
                      update(
                        condition.key,
                        withCompareTo(condition, event.target.checked ? 'dataKey' : 'value'),
                      )
                    }
                    inputProps={{
                      'aria-label': intl.formatMessage(messages.compareToFieldToggle),
                    }}
                  />
                }
                label={intl.formatMessage(messages.compareToFieldToggle)}
              />
            )}

            {/* Says why the toggle is unavailable rather than leaving a disabled
                control with no explanation. */}
            {!unary && field && comparable.length === 0 && (
              <Typography variant="subtitle1" color="textSecondary">
                <FormattedMessage {...messages.noComparableFields} />
              </Typography>
            )}

            {!unary && condition.compareTo === 'dataKey' && (
              <TextField
                id={`${idPrefix}-compare-${condition.key}`}
                label={intl.formatMessage(messages.conditionCompareKeyLabel)}
                value={condition.compareKey}
                onChange={event =>
                  update(condition.key, { ...condition, compareKey: event.target.value })
                }
                error={error === 'no_compare_key'}
                helperText={
                  error === 'no_compare_key'
                    ? intl.formatMessage(messages.conditionNeedsCompareKey)
                    : undefined
                }
                select
                fullWidth
              >
                {comparable.map(key => (
                  <MenuItem key={key} value={key}>
                    {fieldFor(fieldCatalog, key)?.label ?? key}
                  </MenuItem>
                ))}
              </TextField>
            )}

            {!unary && condition.compareTo === 'value' && (
              <TextField
                id={`${idPrefix}-value-${condition.key}`}
                label={intl.formatMessage(messages.conditionValueLabel)}
                value={condition.value}
                onChange={event =>
                  update(condition.key, { ...condition, value: event.target.value })
                }
                error={error === 'no_value'}
                helperText={
                  error === 'no_value'
                    ? intl.formatMessage(messages.conditionNeedsValue)
                    : undefined
                }
                // The input follows the field's type, so a date field gets a date
                // picker and a number field a numeric input rather than everything
                // being free text.
                type={field?.type === 'number' ? 'number' : field?.type === 'date' ? 'date' : 'text'}
                InputLabelProps={field?.type === 'date' ? { shrink: true } : undefined}
                select={Boolean(field?.allowed_values?.length)}
                fullWidth
              >
                {(field?.allowed_values ?? []).map(allowed => (
                  <MenuItem key={allowed} value={allowed}>
                    {allowed}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <IconButton
              size="small"
              aria-label={`${intl.formatMessage(messages.removeCondition)} ${condition.key}`}
              onClick={() =>
                onChange(conditions.filter(row => row.key !== condition.key))
              }
            >
              ×
            </IconButton>
          </div>
        );
      })}

      <Button
        onClick={() =>
          onChange([...conditions, emptyCondition(`${idPrefix}-new-${conditions.length}`)])
        }
      >
        <FormattedMessage {...messages.addCondition} />
      </Button>
    </div>
  );
};
